import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Pencil, Power, PowerOff, X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Percent } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto } from '../utils/format';
import EstadoCuenta from '../components/EstadoCuenta';
import { BotonFotoFactura, ConfirmarProveedorIA } from '../components/CargaFacturaIA';
import BotonAyuda from '../components/BotonAyuda';
import RetencionesProveedor from '../components/RetencionesProveedor';
import { retencionesConfiguradas } from '../utils/retenciones';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

const FORM_VACIO = { nombre: '', rif: '', telefono: '', email: '', direccion: '', dias_credito_default: '' };

export default function Proveedores() {
  const { empresaActiva, empresas, rolActual } = useAuth();
  const puedeConfigurarRetenciones = ['contador', 'gerente', 'admin'].includes(rolActual);
  const [retencionesDe, setRetencionesDe] = useState(null);
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  const [proveedores, setProveedores] = useState([]);
  const [proveedoresTodos, setProveedoresTodos] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  // Desde el Inicio: `proveedorId` + `accion` ('factura' | 'pago') abre ese
  // formulario; `iaResultado` es una factura ya leída por foto. Se usan una
  // sola vez y se borran del historial para que no se repitan al recargar.
  const { state: navState, pathname } = useLocation();
  const navigate = useNavigate();
  const [seleccionadoId, setSeleccionadoId] = useState(navState?.proveedorId ?? null);
  const [accionInicial, setAccionInicial] = useState(navState?.accion ?? null);
  const [listaColapsada, setListaColapsada] = useState(false);
  const [datosColapsados, setDatosColapsados] = useState(true);
  const proveedorSeleccionado = proveedores.find((p) => p.id === seleccionadoId);
  // Carga de factura por IA (#133): `iaSinProveedor` = lectura cuyo RIF no
  // es de ningún proveedor (se confirma el alta); `iaParaForm` = lectura ya
  // ruteada a un proveedor, que su estado de cuenta abre precargada.
  const [iaSinProveedor, setIaSinProveedor] = useState(null); // { resultado, actual }
  const [iaParaForm, setIaParaForm] = useState(null); // { proveedorId, resultado }

  async function cargar(q) {
    const [prov, provTodos] = await Promise.all([
      api.get('/proveedores', { params: { q: q ?? busqueda } }),
      api.get('/proveedores/todas')
    ]);
    setProveedores(prov.data);
    setProveedoresTodos(provTodos.data);
  }

  // Proveedores de otras empresas (para no importar uno que ya es de esta empresa activa).
  const proveedoresImportables = proveedoresTodos.filter((p) => String(p.empresa_id) !== String(empresaActiva));

  function importarDatos(proveedorOrigenId) {
    if (!proveedorOrigenId) return;
    const origen = proveedoresTodos.find((p) => String(p.id) === String(proveedorOrigenId));
    if (!origen) return;
    setForm({
      nombre: origen.nombre, rif: origen.rif || '', telefono: origen.telefono || '', email: origen.email || '',
      direccion: origen.direccion || '',
      dias_credito_default: origen.dias_credito_default ?? ''
    });
  }

  useEffect(() => {
    if (!empresaActiva) return;
    const temporizador = setTimeout(() => cargar(), 300);
    return () => clearTimeout(temporizador);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva, busqueda]);

  useEffect(() => {
    setDatosColapsados(true);
  }, [seleccionadoId]);

  // Desde el buscador general se puede llegar estando ya en Proveedores.
  useEffect(() => {
    if (navState?.proveedorId) setSeleccionadoId(navState.proveedorId);
  }, [navState?.proveedorId]);

  useEffect(() => {
    if (!navState) return;
    if (navState.proveedorId) setListaColapsada(true);
    if (navState.iaResultado) manejarFacturaIA(navState.iaResultado);
    navigate(pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function abrirFacturaIAEn(proveedorId, resultado) {
    setBusqueda('');
    setSeleccionadoId(proveedorId);
    setListaColapsada(true);
    setIaParaForm({ proveedorId, resultado });
  }

  // `actual` = proveedor abierto cuando se usó la cámara desde su estado de
  // cuenta (null si fue desde la barra de Proveedores).
  function manejarFacturaIA(resultado, actual = null) {
    const match = resultado.proveedor_match;
    if (match) {
      if (actual && match.id !== actual.id &&
          !window.confirm(`La factura es de "${match.nombre}", no de "${actual.nombre}". Se va a cargar en "${match.nombre}". ¿Seguir?`)) {
        return;
      }
      abrirFacturaIAEn(match.id, resultado);
      return;
    }
    setIaSinProveedor({ resultado, actual });
  }

  async function proveedorIACreado(id) {
    const { resultado } = iaSinProveedor;
    setIaSinProveedor(null);
    await cargar('');
    abrirFacturaIAEn(id, resultado);
  }

  function empezarEdicion(p) {
    setEditandoId(p.id);
    setForm({
      nombre: p.nombre,
      rif: p.rif || '',
      telefono: p.telefono || '',
      email: p.email || '',
      direccion: p.direccion || '',
      dias_credito_default: p.dias_credito_default ?? ''
    });
    setMostrarForm(true);
  }

  function cancelar() {
    setMostrarForm(false);
    setEditandoId(null);
    setForm(FORM_VACIO);
    setError('');
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    try {
      if (editandoId) {
        await api.put(`/proveedores/${editandoId}`, form);
      } else {
        await api.post('/proveedores', form);
      }
      cancelar();
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar el proveedor');
    }
  }

  async function toggleActivo(proveedor) {
    const accion = proveedor.activo ? 'Inhabilitar' : 'Habilitar';
    if (!window.confirm(`¿${accion} al proveedor "${proveedor.nombre}"?`)) return;
    try {
      await api.put(`/proveedores/${proveedor.id}`, { activo: proveedor.activo ? 0 : 1 });
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || `No se pudo ${accion.toLowerCase()} el proveedor`);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-slate-800">
          Proveedores
          {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <BotonFotoFactura onResultado={(r) => manejarFacturaIA(r)} />
          <button
            onClick={() => setMostrarForm(true)}
            title="Nuevo proveedor"
            className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      {iaSinProveedor && (
        <ConfirmarProveedorIA
          extraccion={iaSinProveedor.resultado}
          proveedorActual={iaSinProveedor.actual}
          onCreado={proveedorIACreado}
          onUsarActual={() => { const { resultado, actual } = iaSinProveedor; setIaSinProveedor(null); abrirFacturaIAEn(actual.id, resultado); }}
          onUsarProveedor={(c) => {
            const { resultado } = iaSinProveedor;
            setIaSinProveedor(null);
            abrirFacturaIAEn(c.id, { ...resultado, proveedor_match: c, duplicado: c.duplicado || null, categoria_sugerida_id: null });
          }}
          onCancelar={() => setIaSinProveedor(null)}
        />
      )}

      {mostrarForm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="font-medium text-slate-600">
                {editandoId ? 'Editando proveedor' : 'Nuevo proveedor'}
              </p>
              <button onClick={cancelar} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={guardar} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {!editandoId && proveedoresImportables.length > 0 && (
                <div className="sm:col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">Importar datos de un proveedor de otra empresa (opcional)</label>
                  <select
                    defaultValue=""
                    onChange={(e) => importarDatos(e.target.value)}
                    className="border rounded-md px-3 py-2 w-full"
                  >
                    <option value="">Escribir a mano...</option>
                    {proveedoresImportables.map((p) => (
                      <option key={p.id} value={p.id}>{p.nombre} ({p.empresa_nombre})</option>
                    ))}
                  </select>
                </div>
              )}

              <input
                required
                placeholder="Nombre del proveedor"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="border rounded-md px-3 py-2 sm:col-span-2"
              />

              <input
                placeholder="RIF (opcional)"
                value={form.rif}
                onChange={(e) => setForm({ ...form, rif: e.target.value })}
                className="border rounded-md px-3 py-2"
              />

              <input
                placeholder="Teléfono (opcional)"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="border rounded-md px-3 py-2"
              />

              <input
                type="email"
                placeholder="Email (opcional)"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="border rounded-md px-3 py-2 sm:col-span-2"
              />

              <input
                placeholder="Dirección fiscal (opcional)"
                value={form.direccion}
                onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                className="border rounded-md px-3 py-2 sm:col-span-2"
              />

              <div>
                <label className="block text-xs text-slate-500 mb-1">Días de crédito por defecto (opcional)</label>
                <input
                  type="number"
                  min="0"
                  placeholder="Ej. 30"
                  value={form.dias_credito_default}
                  onChange={(e) => setForm({ ...form, dias_credito_default: e.target.value })}
                  className="border rounded-md px-3 py-2 w-full"
                />
              </div>

              <p className="text-xs text-slate-400 sm:col-span-2">
                Se precarga al cargar una factura de este proveedor; se puede cambiar por factura. Junto con la fecha de la factura arma la fecha de vencimiento en Cuentas por Pagar.
                <br />El saldo por pagar se calcula automáticamente a partir de las facturas de compra y sus pagos.
              </p>

              {error && <p className="text-red-600 text-sm sm:col-span-2">{error}</p>}

              <button type="submit" className="bg-primary-600 text-white rounded-md py-2 sm:col-span-2 hover:bg-primary-700">
                {editandoId ? 'Guardar cambios' : 'Guardar'}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <div className={`bg-white rounded-xl shadow-sm overflow-hidden shrink-0 transition-all ${listaColapsada ? 'w-11' : seleccionadoId ? 'w-full lg:max-w-[30%]' : 'w-full lg:max-w-[70%]'}`}>
          <div className="flex items-center justify-between p-2 border-b">
            {!listaColapsada && <span className="text-xs font-medium text-slate-500 px-1">Proveedores</span>}
            <button
              onClick={() => setListaColapsada(!listaColapsada)}
              title={listaColapsada ? 'Expandir lista' : 'Minimizar lista'}
              className="p-1 rounded-md text-slate-400 hover:text-primary-600 hover:bg-slate-50 transition-colors ml-auto"
            >
              {listaColapsada ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>
          {!listaColapsada && (
            <div className="p-2 border-b">
              <input
                placeholder="Buscar por nombre o producto..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="border rounded-md px-3 py-2 w-full text-sm"
              />
            </div>
          )}
          {!listaColapsada && (
            <table className="w-full text-sm table-fixed">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="p-3">Nombre</th>
                  <th className="p-3 text-right w-24">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {proveedores.map((p) => (
                  <tr
                    key={p.id}
                    {...filaClickeable(() => { setSeleccionadoId(p.id); setListaColapsada(true); })}
                    className={`border-t hover:bg-slate-50 transition-colors cursor-pointer ${seleccionadoId === p.id ? 'bg-primary-50' : ''} ${FOCO_FILA}`}
                  >
                    <td className="p-3 font-medium break-words">{p.nombre}</td>
                    <td className={`p-3 text-right whitespace-nowrap ${Number(p.pendiente_pagar_usd) < -0.02 ? 'text-emerald-600' : ''}`}>
                      ${formatMonto(Math.abs(Number(p.pendiente_pagar_usd)))}{Number(p.pendiente_pagar_usd) < -0.02 ? ' a favor' : ''}
                    </td>
                  </tr>
                ))}
                {proveedores.length === 0 && (
                  <tr>
                    <td colSpan={2} className="p-6 text-center text-slate-400">
                      {busqueda ? 'Ningún proveedor coincide con la búsqueda.' : 'No hay proveedores creados todavía.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {seleccionadoId && proveedorSeleccionado && (
        <div className="w-full lg:flex-1 bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-medium text-slate-700">{proveedorSeleccionado.nombre}</h2>
                  <span
                    className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                      proveedorSeleccionado.activo ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {proveedorSeleccionado.activo ? 'Activo' : 'Inhabilitado'}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button
                    onClick={() => empezarEdicion(proveedorSeleccionado)}
                    title="Editar"
                    className="text-slate-400 hover:text-primary-600 transition-colors"
                  >
                    <Pencil size={16} />
                  </button>
                  {puedeConfigurarRetenciones && (
                    <button
                      onClick={() => setRetencionesDe(proveedorSeleccionado)}
                      title={retencionesConfiguradas(proveedorSeleccionado) ? 'Retenciones' : 'Retenciones (sin configurar)'}
                      className={`relative transition-colors hover:text-primary-600 ${retencionesConfiguradas(proveedorSeleccionado) ? 'text-slate-400' : 'text-amber-500'}`}
                    >
                      <Percent size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => toggleActivo(proveedorSeleccionado)}
                    title={proveedorSeleccionado.activo ? 'Inhabilitar' : 'Habilitar'}
                    className={`transition-colors ${proveedorSeleccionado.activo ? 'text-slate-400 hover:text-red-600' : 'text-slate-400 hover:text-green-600'}`}
                  >
                    {proveedorSeleccionado.activo ? <PowerOff size={16} /> : <Power size={16} />}
                  </button>
                  <button
                    onClick={() => setDatosColapsados(!datosColapsados)}
                    title={datosColapsados ? 'Mostrar datos de la compañía' : 'Minimizar datos de la compañía'}
                    className="text-slate-400 hover:text-primary-600 transition-colors"
                  >
                    {datosColapsados ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                  </button>
                </div>
              </div>

              {!datosColapsados && (
                <div className="p-4 border-b grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">RIF</p>
                    <p className="text-slate-700">{proveedorSeleccionado.rif || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Teléfono</p>
                    <p className="text-slate-700">{proveedorSeleccionado.telefono || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Email</p>
                    <p className="text-slate-700">{proveedorSeleccionado.email || '-'}</p>
                  </div>
                  <div className="sm:col-span-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Dirección fiscal</p>
                    <p className="text-slate-700">{proveedorSeleccionado.direccion || '-'}</p>
                  </div>
                  <div className="sm:col-span-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Productos que suministra</p>
                    <p className="text-slate-700">{proveedorSeleccionado.productos_suministrados || '-'}</p>
                  </div>
                </div>
              )}

              <EstadoCuenta
                parte="proveedor"
                entidad={proveedorSeleccionado}
                empresaNombre={empresaNombre}
                onSaldoCambiado={cargar}
                extraccionPendiente={iaParaForm?.proveedorId === seleccionadoId ? iaParaForm.resultado : null}
                onExtraccionUsada={() => setIaParaForm(null)}
                onFacturaIA={(r) => manejarFacturaIA(r, proveedorSeleccionado)}
                accionInicial={accionInicial}
                onAccionInicialUsada={() => setAccionInicial(null)}
              />
        </div>
        )}
      </div>
      {retencionesDe && (
        <RetencionesProveedor
          proveedor={retencionesDe}
          onCerrar={() => setRetencionesDe(null)}
          onGuardado={() => { setRetencionesDe(null); cargar(); }}
        />
      )}
    </div>
  );
}
